import { Box, CircularProgress, Typography } from "@mui/material";
import { useEffect, useRef } from "react";

import { GeneratingLoadingState } from "../schema";
import styles from "../styles";

const STEPS: { label: string; key: keyof GeneratingLoadingState }[] = [
  { label: "Reading your portfolio", key: "portfolio" },
  { label: "Attempting to learning your writing style", key: "emails" },
  {
    label: "Pulling information from your social media accounts",
    key: "socialMedia",
  },
  { label: "Generating your pitch", key: "generateTemplate" },
];

interface Props {
  loading: GeneratingLoadingState;
  errors?: Partial<Record<keyof GeneratingLoadingState, string>>;
  viewMode?: "desktop" | "mobile";
  showPortfolio?: boolean;
  showEmails?: boolean;
  showSocialMedia?: boolean;
}
export function GeneratingScreen({
  loading,
  errors,
  viewMode = "desktop",
  showPortfolio = true,
  showEmails = true,
  showSocialMedia = true,
}: Props) {
  const isMobile = viewMode === "mobile";
  // Remember which steps have been loading at least once so we can render
  // them as "done" once they flip back to false. Per-step state lets multiple
  // steps run in parallel — each row renders independently of the others.
  const startedRef = useRef<Set<keyof GeneratingLoadingState>>(new Set());
  useEffect(() => {
    STEPS.forEach((step) => {
      if (loading[step.key]) {
        startedRef.current.add(step.key);
      }
    });
  }, [loading]);

  // Some steps are skipped on the backend depending on what the user filled
  // in (portfolio URL, Gmail tone source, social URLs) — hide their rows
  // entirely so the user doesn't see steps that will never run.
  const visibleSteps = STEPS.filter((step) => {
    if (step.key === "portfolio") return showPortfolio;
    if (step.key === "emails") return showEmails;
    if (step.key === "socialMedia") return showSocialMedia;
    return true;
  });

  return (
    <Box sx={styles.generatingContainer(isMobile)}>
      <Box sx={styles.generatingSpinnerWrapper(isMobile)}>
        <Box sx={styles.generatingSpinnerTrack} />
        <CircularProgress
          size={isMobile ? 80 : 96}
          thickness={4}
          sx={styles.generatingSpinnerProgress}
        />
      </Box>

      <Typography sx={styles.generatingTitle(isMobile)}>
        Crafting your pitch
      </Typography>
      <Typography sx={styles.generatingSubtitle(isMobile)}>
        This takes about a minute.
      </Typography>

      <Box sx={styles.generatingStepsList(isMobile)}>
        {visibleSteps.map((step) => {
          const active = !!loading[step.key];
          const errorMessage = errors?.[step.key];
          const done =
            !active && startedRef.current.has(step.key) && !errorMessage;
          const errored = !active && !!errorMessage;
          return (
            <Box
              key={step.key}
              sx={styles.generatingStepRow(isMobile, active || errored)}
            >
              <Box sx={{ display: "flex", flexDirection: "column", flex: 1 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                  {errored ? (
                    <Box
                      component="i"
                      className="fa-solid fa-circle-exclamation"
                      sx={{
                        fontSize: isMobile ? 16 : 20,
                        color: "#C0392B",
                      }}
                    />
                  ) : done ? (
                    <Box
                      component="i"
                      className="fa-solid fa-circle-check"
                      sx={styles.generatingStepIconDone(isMobile)}
                    />
                  ) : active ? (
                    <CircularProgress
                      size={isMobile ? 16 : 20}
                      thickness={5}
                      sx={styles.generatingStepIconActive(isMobile)}
                    />
                  ) : (
                    <Box sx={styles.generatingStepIconPending(isMobile)} />
                  )}
                  <Typography
                    sx={styles.generatingStepLabel(
                      isMobile,
                      done || active || errored,
                    )}
                  >
                    {step.label}
                  </Typography>
                </Box>
                {errored && (
                  <Typography
                    sx={{
                      mt: 0.5,
                      ml: isMobile ? 3.5 : 4.25,
                      fontSize: isMobile ? 12 : 13,
                      color: "#C0392B",
                    }}
                  >
                    {errorMessage}
                  </Typography>
                )}
              </Box>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
}
